import React, { useState, useEffect, useCallback } from 'react';
import { FaBullseye, FaEdit } from 'react-icons/fa';
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line } from 'recharts';
import Modal from '../../components/Modal';
import { getGoalsForMonth, updateGoalTarget } from '../../services/goalsService';
import PortalBackground from '../../components/PortalBackground';

const InternalGoals = () => {
  const [activeTab, setActiveTab] = useState('Company');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [newTarget, setNewTarget] = useState('');
  const [goals, setGoals] = useState({ Company: [], Leads: [], Sales: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });

  const fetchGoals = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedGoals = await getGoalsForMonth(currentDate.year, currentDate.month);
      setGoals({
        Company: fetchedGoals.company,
        Leads: fetchedGoals.leads,
        Sales: fetchedGoals.sales,
      });
    } catch (error) {
      console.error("Error fetching goals: ", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const openModal = (goal) => {
    setSelectedGoal(goal);
    const initialTarget = goal.unit === 'usd' ? `$${goal.target.toLocaleString()}` : goal.unit === 'percent' ? `${goal.target}%` : goal.target;
    setNewTarget(initialTarget);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedGoal(null);
  };

  const handleTargetChange = (e) => {
    setNewTarget(e.target.value);
  };

  const handleTargetSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal) return;

    // Convert formatted string back to a number
    const targetValue = Number(String(newTarget).replace(/[^0-9.-]+/g,""));

    try {
      await updateGoalTarget(selectedGoal.id, targetValue);
      // Refresh the goals from Firestore to show the update
      fetchGoals();
    } catch (error) {
      console.error("Error updating goal target: ", error);
    }

    closeModal();
  };
  
  const formatValue = (value, unit) => {
    switch (unit) {
      case 'usd':
        return `$${Number(value).toLocaleString()}`;
      case 'percent':
        return `${value}%`;
      default:
        return Number(value).toLocaleString();
    }
  };

  const calculateProgress = (actual, target) => {
    if (target === 0) return 0;
    return Math.min(100, Math.round((actual / target) * 100));
  };

  const tabs = ['Company', 'Leads', 'Sales'];
  const currentGoals = goals[activeTab] || [];

  return (
    <div className="min-h-screen relative">
      <div className="flex justify-center items-center mb-6">
      <PortalBackground />
      <div className="relative">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
          {new Date(currentDate.year, currentDate.month - 1).toLocaleString('default', { month: 'long' })} {currentDate.year} Goals
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 justify-center">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
              activeTab === tab
                ? 'bg-red-600 text-white border-b-2 border-red-600'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Full Width Goal Cards */}
      <div className="space-y-6">
        {isLoading ? (
          <p className="text-center text-gray-500 dark:text-gray-400">Loading goals...</p>
        ) : (
          currentGoals.map((goal) => {
            const progress = calculateProgress(goal.actual, goal.target);
            return (
              <div 
                key={goal.id} 
                className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all"
                onClick={() => openModal(goal)}
              >
                <div className="flex justify-center items-start mb-4">
                  <div className="text-center">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{goal.metricName}</h3>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">{goal.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatValue(goal.target, goal.unit)}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Target</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">{formatValue(goal.actual, goal.unit)}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Current</div>
                  </div>
                  <div className="text-center">
                     <div className={`text-2xl font-bold ${
                      progress >= 100 ? 'text-green-600 dark:text-green-400' : 
                      progress >= 70 ? 'text-yellow-600 dark:text-yellow-400' : 
                      'text-red-600 dark:text-red-400'
                    }`}>
                      {progress}%
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Reached</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-6">
                  <div 
                    className={`h-3 rounded-full ${
                      progress >= 90 ? 'bg-green-500 dark:bg-green-400' :
                      progress >= 70 ? 'bg-yellow-500 dark:bg-yellow-400' :
                      'bg-red-500 dark:bg-red-400'
                    }`}
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {currentGoals.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <FaBullseye className="text-gray-400 dark:text-gray-600 text-4xl mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400 text-lg">No goals found for {activeTab}</p>
          <p className="text-gray-400 dark:text-gray-500">Goals will be added for this category soon.</p>
        </div>
      )}
      
      {selectedGoal && (
        <Modal isOpen={isModalOpen} onClose={closeModal}>
          <div className="flex items-center justify-center mb-4">
            <FaBullseye className="text-red-600 mr-3 text-2xl" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedGoal.metricName}</h2>
          </div>
          <form onSubmit={handleTargetSubmit}>
            <div className="mb-4">
              <label htmlFor="newTarget" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                New Target ({selectedGoal.unit})
              </label>
              <input
                type="text"
                id="newTarget"
                value={newTarget}
                onChange={handleTargetChange}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm text-gray-900 dark:text-white"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={closeModal}
                className="mr-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700"
              >
                Set Target
              </button>
            </div>
          </form>
        </Modal>
      )}
      </div>
    </div>
  );
};

export default InternalGoals; 